import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormControl, FormGroup } from "@angular/forms";
import { AbstractDocumentModel } from "../models/abstract-document-model";
import { EditDocumentPopup, OpenDocumentPopupReason } from "../models/edit-document-popup.model";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable, forkJoin } from "rxjs";
import { DocumentConfig } from "@models/documents/DocumentConfig";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";

@Component({
    template: "",
    standalone: false
})
export abstract class AbstractEditDocumentComponent implements OnInit {
    protected dialogRef: MatDialogRef<EditDocumentPopup, AbstractDocumentModel[]> =
        inject(MatDialogRef);
    @Input() public dialogData: EditDocumentPopup = inject(MAT_DIALOG_DATA);
    @Output() public emitDownload = new EventEmitter<AbstractDocumentModel>();
    @Output() public emitDelete = new EventEmitter<AbstractDocumentModel>();
    protected readonly destroyRef = inject(DestroyRef);

    protected documentTypes: SelectOptionModel[] = [];
    protected documentConfig: DocumentConfig;

    protected dialogTitle = "";
    protected btnLabel = "";

    protected documentForm: FormGroup = new FormGroup({
        documentTypeId: new FormControl(null),
        description: new FormControl(null),
    });

    protected historyLoaded: boolean = false;
    protected historyDocuments: AbstractDocumentModel[] = [];

    abstract getDocumentTypes(): Observable<SelectOptionModel[]>;
    abstract handleDocumentTypes(documentTypes: SelectOptionModel[]): SelectOptionModel[];
    abstract getDocumentConfig(): Observable<DocumentConfig>;
    abstract handleDocumentConfig(documentConfig: DocumentConfig): DocumentConfig;
    abstract getDocumentHistory(documentId: string): Observable<AbstractDocumentModel[]>;
    abstract handleDocumentHistory(history: AbstractDocumentModel[]): AbstractDocumentModel[];
    abstract validateUploadLimit(files: File[]): boolean;
    abstract filterAllowedFiles(files: FileList): File[];
    abstract getDocumentIcon(name: string): string;

    ngOnInit(): void {
        this.loadData();
        this.initLabelsAndFormControls();
    }

    protected loadData() {
        forkJoin([
            this.getDocumentTypes().pipe(takeUntilDestroyed(this.destroyRef)),
            this.getDocumentConfig().pipe(takeUntilDestroyed(this.destroyRef)),
            this.getDocumentHistory(this.dialogData.documents[0].id).pipe(
                takeUntilDestroyed(this.destroyRef)
            ),
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([documentTypes, documentConfig, history]) => {
                this.documentTypes = this.handleDocumentTypes(documentTypes);
                this.documentConfig = this.handleDocumentConfig(documentConfig);
                this.historyDocuments = this.handleDocumentHistory(history);

                this.historyLoaded = true;
            });
    }

    protected initLabelsAndFormControls() {
        if (this.dialogData.openReason === OpenDocumentPopupReason.add) {
            this.dialogTitle = "Add document(s)";
            this.btnLabel = "Add document(s)";
        } else if (this.dialogData.openReason === OpenDocumentPopupReason.edit) {
            this.dialogTitle = "Edit document";
            this.btnLabel = "Apply changes";

            this.documentForm = new FormGroup({
                documentTypeId: new FormControl(this.dialogData.documents[0].documentTypeId),
                description: new FormControl(this.dialogData.documents[0].description),
            });
        } else {
            this.dialogTitle = "View document";

            this.documentForm = new FormGroup({
                documentTypeId: new FormControl({
                    value: this.dialogData.documents[0].documentTypeId,
                    disabled: true,
                }),
                description: new FormControl({
                    value: this.dialogData.documents[0].description,
                    disabled: true,
                }),
            });
        }
    }

    protected onFileSelection(selection: { fileList: FileList; isBrowse: boolean }) {
        if (selection.fileList?.length) {
            const files = this.filterAllowedFiles(selection.fileList);

            if (files.length && this.validateUploadLimit(files)) {
                const newDocuments: AbstractDocumentModel[] = [];

                if (this.dialogData.openReason === "Edit") {
                    this.dialogData.documents = [
                        {
                            name: files[0].name,
                            file: files[0],
                            fileIcon: this.getDocumentIcon(files[0].name),
                        },
                    ];
                } else {
                    files.forEach((file) => {
                        newDocuments.push({
                            name: file.name,
                            file: file,
                            fileIcon: this.getDocumentIcon(file.name),
                        });
                    });

                    this.dialogData.documents = [...newDocuments];
                }
            }
        }
    }

    protected onRemoveFile(document: AbstractDocumentModel) {
        let index = this.dialogData.documents.findIndex((d) => d === document);
        this.dialogData.documents.splice(index, 1);
        this.emitDelete.emit(document);
        if (this.dialogData.showDelete) {
            this.onApplyChanges();
        }
    }

    protected onApplyChanges() {
        if (this.documentForm.valid) {
            this.dialogData.documents.forEach((document) => {
                document.documentTypeId = this.documentForm.value.documentTypeId;
                document.description = this.documentForm.value.description;
            });
            this.close(this.dialogData.documents);
        }
    }

    public onDownload(document: AbstractDocumentModel) {
        this.emitDownload.emit(document);
    }

    public close(result: AbstractDocumentModel[]) {
        this.dialogRef.close(result);
    }
}
